import React from 'react';
import { PinInput as ArkPinInput } from '@ark-ui/react/pin-input';
import clsx from 'clsx';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export interface PinInputProps extends BaseInputProps<string> {
  /** Počet polí PINu. */
  length?: number;
  /** Maskovat zadané znaky (např. pro OTP/hesla). */
  mask?: boolean;
  /** OTP režim (`one-time-code` autocomplete + numerická klávesnice). */
  otp?: boolean;
  /**
   * Lokalizovaný popisek jednotlivého políčka pro AT — dostane index (0-based)
   * a celkový počet. Default je český.
   */
  inputLabel?: (index: number, length: number) => string;
  className?: string;
}

/**
 * Atomický PIN/OTP input postavený na Ark UI `PinInput`.
 *
 * RHF drží hodnotu jako spojený `string`; Ark pracuje s polem znaků, proto
 * mapujeme `value` ↔ `value.split('')` a zpět přes `details.valueAsString`.
 *
 * `ids.label` míří na popisek z `<FormField />`, takže skupina políček je
 * pojmenovaná pro AT; viditelná políčka dostávají popisky přes
 * `translations.inputLabel`. QA atributy zůstávají na skrytém inputu, který
 * nese spojenou hodnotu.
 */
export const PinInput: React.FC<PinInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  describedBy,
  labelId,
  testAttributes,
  length = 4,
  mask,
  otp,
  inputLabel = (index, total) => `Znak ${index + 1} z ${total}`,
  className,
}) => {
  // Ark čte hodnotu jednotlivých políček přímo z `value[index]`; pokud je pole
  // kratší než počet políček (`length`), zapíše do zbylých `undefined`, takže
  // se v UI zobrazí literal "undefined". Proto hodnotu vždy doplníme na délku.
  const chars = value ? value.split('') : [];
  const paddedValue = Array.from({ length }, (_, index) => chars[index] ?? '');

  return (
    <ArkPinInput.Root
      ids={{ hiddenInput: id, label: labelId }}
      value={paddedValue}
      count={length}
      disabled={disabled}
      readOnly={readOnly}
      invalid={hasError}
      mask={mask}
      otp={otp}
      translations={{ inputLabel }}
      onValueChange={(details) => onChange(details.valueAsString)}
      className={clsx(className)}
    >
      <ArkPinInput.Control className={styles.pinControl}>
        {Array.from({ length }, (_, index) => (
          <ArkPinInput.Input
            key={index}
            index={index}
            onBlur={onBlur}
            className={styles.pinInput}
            aria-describedby={describedBy}
          />
        ))}
      </ArkPinInput.Control>
      <ArkPinInput.HiddenInput ref={ref} name={name} {...testAttributes} />
    </ArkPinInput.Root>
  );
};

PinInput.displayName = 'PinInput';
